import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  addSharedContact,
  addSharedContactUserPortal,
} from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { SharedContactModel } from '../../../../types';
import SharedDialog from '../../../shared/shared-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { Controller, useForm } from 'react-hook-form';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { useUserSettings } from '../../../utils/use-user-settings';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
interface AddSharedContactsProps {
  open: boolean;
  handleClose: () => void;
  page: number;
  pageSize: number;
  userView?: boolean;
}

export default function AddSharedContactsDialog({
  open,
  handleClose,
  page,
  pageSize,
  userView,
}: AddSharedContactsProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();

  const { primaryDomain, changeLogRoles } = useUserSettings();

  interface FormInput {
    firstName: string;
    lastName: string;
    email: string;
  }

  const {
    handleSubmit,
    control,
    reset,
    getValues,
    formState: { errors, isDirty },
  } = useForm<FormInput>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
    },
  });
  const onSubmit = (data: FormInput) => {
    if (
      !userView &&
      isAbleToSeeUIElement(
        changeLogRoles,
        DirectoryRoles.AddSharedContacts.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      const emailArray = data.email.split('@');
      const createdContact: Partial<SharedContactModel> = {
        userName: emailArray[0],
        userDomain: emailArray[1],
        firstName: data.firstName,
        lastName: data.lastName,
        groupHref: [],
        emails: [
          {
            rel: 'Primary',
            val: data.email,
            addrVal: null,
            orgVal: null,
          },
        ],
        updated: 0,
        hasBeenDeleted: false,
        extendedProperties: [],
      };
      if (userView) {
        userPortalMutate(createdContact);
      } else {
        mutate(createdContact);
      }
    }
  };
  const { mutate, isPending } = useMutation({
    mutationFn: (data: Partial<SharedContactModel>) =>
      addSharedContact(primaryDomain, data, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Contact added successfully!',
      });

      queryClient.invalidateQueries({
        queryKey: [UserKeys.SharedContacts, page, pageSize, ''],
      });
      setChangeLogMessage('');
      handleClose();
      reset();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue creating the contact. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.directory.shared_contacts.create_shared_contact, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.sharedContacts,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const { mutate: userPortalMutate, isPending: userPortalPending } =
    useMutation({
      mutationFn: (data: Partial<SharedContactModel>) =>
        addSharedContactUserPortal(primaryDomain, data),
      onSuccess: () => {
        setSnackbar({
          open: true,
          severity: 'success',
          message: 'Contact added successfully!',
        });

        queryClient.invalidateQueries({
          queryKey: [UserKeys.SharedContacts, page, pageSize, ''],
        });
        handleClose();
        reset();
      },
      onError: (error) => {
        handleErrors({
          error,
          snackbarMessage:
            'There was an issue creating the contact. Please refresh and try again.',
        });
      },
      onSettled: () => {
        createEvent(analytics.userView.shared_contacts.create_shared_contact, {
          module: ModuleList.modules.userView.name,
          section: ModuleList.modules.userView.sections.sharedContacts,
        });
      },
    });

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        reset();
        handleClose();
      }}
      title="Create Contact"
      dialogContent={
        <Stack gap={2} sx={{ mt: 1 }}>
          <Controller
            name={'firstName'}
            control={control}
            render={({ field }) => (
              <TextField
                inputProps={{
                  id: 'firstName',
                }}
                InputLabelProps={{
                  htmlFor: 'firstName',
                }}
                label="First Name"
                fullWidth
                autoFocus
                autoComplete="off"
                {...field}
              />
            )}
          />
          <Controller
            name={'lastName'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                inputProps={{
                  id: 'lastName',
                }}
                InputLabelProps={{
                  htmlFor: 'lastName',
                }}
                label="Last Name"
                fullWidth
                autoComplete="off"
              />
            )}
          />
          <Controller
            name={'email'}
            control={control}
            rules={{
              required: 'Email is required.',
            }}
            render={({ field }) => (
              <TextField
                {...field}
                inputProps={{
                  id: 'email',
                }}
                InputLabelProps={{
                  htmlFor: 'email',
                }}
                label="Email"
                fullWidth
                autoComplete="off"
                error={!!errors.email}
                helperText={errors?.email?.message || ' '}
                required
              />
            )}
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              const emailArray = getValues('email').split('@');
              const createdContact: Partial<SharedContactModel> = {
                userName: emailArray[0],
                userDomain: emailArray[1],
                firstName: getValues('firstName'),
                lastName: getValues('lastName'),
                groupHref: [],
                emails: [
                  {
                    rel: 'Primary',
                    val: getValues('email'),
                    addrVal: null,
                    orgVal: null,
                  },
                ],
                updated: 0,
                hasBeenDeleted: false,
                extendedProperties: [],
              };
              mutate(createdContact);
            }}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        loading: isPending || userPortalPending,
        action: handleSubmit(onSubmit),
        disabled: isPending || !isDirty || userPortalPending,
        title: 'Add Contact',
      }}
    />
  );
}
